import React, { useState, useEffect, useRef } from 'react';

const ForkliftsWhyChoose = () => {
  const [counters, setCounters] = useState({
    years: 0,
    countries: 0,
    specs: 0
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  // Intersection Observer for triggering animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.6 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  const animateCounters = () => {
    const duration = 1500;
    const frameRate = 1000 / 60;
    const totalFrames = Math.round(duration / frameRate);
    
    const targets = {
      years: 46,
      countries: 90,
      specs: 200
    };

    let frame = 0;

    const counter = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      const easeProgress = 1 - Math.pow(1 - progress, 4);

      setCounters({
        years: Math.round(easeProgress * targets.years),
        countries: Math.round(easeProgress * targets.countries),
        specs: Math.round(easeProgress * targets.specs)
      });

      if (frame === totalFrames) {
        clearInterval(counter);
        setCounters(targets);
      }
    }, frameRate);
  };

  return (
    <section ref={sectionRef} className="whyChoose-section">
      <div className="whyChoose-container">
        {/* Main Heading */}
        <h2 className="whyChoose-heading">
          Why Choose <span className="whyChoose-heading-bold">UN Forklifts?</span>
        </h2>

        <div className="whyChoose-content">
          {/* Left Side - Stats and Description */}
          <div className="whyChoose-left">
            {/* Stats Grid */}
            <div className="stats-grid">
              {/* Founded in 1978 */}
              <div className="stat-card stat-card-years">
                <p className="stat-label">Founded in 1978</p>
                <p className="stat-value">
                  {counters.years}
                  <span className="stat-unit">Years</span>
                </p>
              </div>

              {/* Global Presence */}
              <div className="stat-card stat-card-countries">
                <p className="stat-label">Global Presence</p>
                <p className="stat-value">
                  {counters.countries}+
                  <span className="stat-unit">Countries</span>
                </p>
              </div>

              {/* Wide Product Range */}
              <div className="stat-card stat-card-specs">
                <p className="stat-label">Wide Product Range</p>
                <p className="stat-value">
                  {counters.specs}+
                  <span className="stat-unit">Specs</span>
                </p>
              </div>
            </div>

            {/* Description Text */}
            <div className="whyChoose-description">
              <p>
                UN Forklift is one of the <strong>world's leading manufacturers</strong> of material-handling equipment, with operations in over <strong>90+ countries.</strong>
              </p>
              <p>
                Guided by safety, efficiency, and reliability, UN delivers advanced{' '}
                <strong>Diesel, Electric, & Lithium-ion forklifts</strong> to industries worldwide.
              </p>
              <p>
                Now, <strong>Golden Sparrow</strong> proudly brings this global expertise to the UAE.
              </p>
            </div>
          </div>

          {/* Right Side - Logo/Image */}
          <div className="whyChoose-right">
            <div className="logo-container">
              <img
                src="./images/logos/unforklift.png"
                alt="UN Forklift Logo"
                className="logo-image"
              />
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`

/* Section Container */
.whyChoose-section {
  padding: 2rem 0.5rem;
  background-color: #ffffff;
}

@media (min-width: 768px) {
  .whyChoose-section {
    padding: 4rem 0;
  }
}

/* Main Container */
.whyChoose-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 clamp(1rem, 4vw, 2rem);
}

/* Main Heading */
.whyChoose-heading {
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  font-weight: 400;
  margin-bottom: 2rem;
  text-align: left;
  color: #000000;
  line-height: 1.3;
}

.whyChoose-heading-bold {
  font-weight: 600;
}

@media (min-width: 768px) {
  .whyChoose-heading {
    text-align: left;
    margin-bottom: 3rem;
  }
}

/* Content Layout */
.whyChoose-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

@media (min-width: 1024px) {
  .whyChoose-content {
    flex-direction: row;
    gap: 3rem;
  }
}

/* Left Section */
.whyChoose-left {
  width: 100%;
}

@media (min-width: 1024px) {
  .whyChoose-left {
    width: 70%;
  }
}


/* Stats Grid - UPDATED FOR MOBILE 2-COLUMN LAYOUT */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* 2 columns on mobile */
  gap: 1rem;
  margin-bottom: 2rem;
  text-align: center;
}

@media (min-width: 768px) {
  .stats-grid {
    gap: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .stats-grid {
    grid-template-columns: 2fr 1fr 3fr; /* 3 columns with custom widths on desktop */
    gap: 1.2rem;
  }
}

/* Stat Card Base Styles */
.stat-card {
  background: linear-gradient(159.21deg, rgba(255, 210, 0, 0.23) 11.19%, rgba(247, 151, 30, 0.23) 101.23%);
  border-radius: 0.5rem;
  padding: clamp(1rem, 3vw, 1.25rem);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(195, 150, 2, 0.15);
}

/* Mobile Grid Layout */
.stat-card-years {
  grid-column: span 1; /* Takes 1 column (left side) */
}

.stat-card-countries {
  grid-column: span 1; /* Takes 1 column (right side) */
}

.stat-card-specs {
  grid-column: 1 / -1; /* Spans full width (both columns) */
}

/* Desktop Grid Layout */
@media (min-width: 1024px) {
  .stat-card-years,
  .stat-card-countries,
  .stat-card-specs {
    grid-column: span 1; /* Each takes 1 column on desktop */
  }
}

/* Stat Label */
.stat-label {
  font-size: clamp(1rem, 2.5vw, 1.15rem);
  font-weight: 500;
  color: #000000;
  margin-bottom: 0.5rem;
}

/* Stat Value */
.stat-value {
  font-size: clamp(2rem, 6vw, 3.5rem);
  font-weight: 600;
  color: #000000;
  line-height: 1.2;
}

/* Stat Unit */
.stat-unit {
  font-size: clamp(1.25rem, 2vw, 1.75rem);
  font-weight: 400;
  margin-left: 0.5rem;
}

/* Description Section */
.whyChoose-description {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: #000000;
  line-height: 1.7;
}

.whyChoose-description p {
  font-size: clamp(1.25rem, 2.5vw, 1.25rem);
  margin: 0;
}

.whyChoose-description strong {
  font-weight: 700;
  color: #000000;
}

/* Right Section - Logo */
.whyChoose-right {
  width: 100%;
  display: flex;
  justify-content: center;
}

@media (max-width: 639px) {
  .whyChoose-right {
    display: none;
  }
}

@media (min-width: 1024px) {
  .whyChoose-right {
    width: 30%;
    justify-content: flex-end;
  }
}

/* Logo Container */
.logo-container {
  width: 100%;
  max-width: 24rem; /* 384px */
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: clamp(200px, 30vw, 252px);
}

.logo-image:hover {
  transform: scale(1.02);
  box-shadow: 0px 24px 48px rgba(195, 150, 2, 0.18);
}

/* Logo Image */
.logo-image {
  width: 100%;
  height: auto;
  object-fit: contain;
  padding: clamp(1.5rem, 4vw, 2rem);
  border: 3px solid transparent;
  border-radius: 0.5rem;
  background-image:
    linear-gradient(white, white),
    linear-gradient(135deg, #EEBC70, #E3A455, #FFEBC4, #D0A068);
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: 0px 20px 40px rgba(195, 150, 2, 0.12);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

/* Animation for counter appearance */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card {
  animation: fadeInUp 0.6s ease-out forwards;
}

.stat-card-years {
  animation-delay: 0.1s;
}

.stat-card-countries {
  animation-delay: 0.2s;
}

.stat-card-specs {
  animation-delay: 0.3s;
}

/* Accessibility - Reduce motion for users who prefer it */
@media (prefers-reduced-motion: reduce) {
  .stat-card,
  .logo-container {
    animation: none;
    transition: none;
  }
  
  .stat-card:hover,
  .logo-container:hover {
    transform: none;
  }
}

/* Print Styles */
@media print {
  .whyChoose-section {
    padding: 1rem 0;
  }
  
  .stat-card,
  .logo-container {
    box-shadow: none;
    border: 1px solid #ccc;
  }
}
      `}</style>
    </section>
  );
};

export default ForkliftsWhyChoose;